import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { makeStyles } from "@material-ui/core/styles";
import LoadingState from "components/items/loading-state";
import { CloudFileUpload } from "helpers/icon-finder";
import { Typography } from "@material-ui/core";
import { formatFileSize } from "helpers/format-file-size-byte";

// File size constants
// Default Maximum file size set to 100 MB which means basically no limit
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

const FILE_REJECTION_CODES = {
  fileTooLarge: "file-too-large", // a single file size exceeds MAX_FILE_SIZE_BYTES
  totalSizeExceeded: "total-size-exceeded", // cumulative file size exceeds MAX_FILE_SIZE_BYTES
};

const useStyles = makeStyles(theme => ({
  dropZone: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    position: "relative", // Ensure that children with absolute positioning (e.g., the native input element) are relative to this container to not break layout
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#777",
    outline: "none",
    transition: "border .24s ease-in-out",
    justifyContent: "center",
    cursor: "pointer",
  },
  dropZoneFocused: {
    borderColor: "#2196f3",
  },
  dropZoneAccept: {
    borderColor: "#00e676",
  },
  dropZoneReject: {
    borderColor: "#ff1744",
  },
}));

function DropzoneAttachment({
  onFileUpload,
  disabled = false,
  loading,
  disabledLabel,
  label,
  helperText,
  extraParameters,
  isAcceptAllFormat = false,
  acceptFormat = {
    "application/pdf": [".pdf"],
  },
  // NEW: default to single file upload
  // If you want to allow multiple files, set multiple to true
  // and pass maxFiles to limit the number of files
  multiple = false,
  // Optional cap for multi-upload; ignored when multiple=false
  maxFiles,
  maxFileSizeBytes = MAX_FILE_SIZE_BYTES,
  totalUploadedSize = 0,
}) {
  const classes = useStyles();

  // Store totalUploadedSize in a ref to avoid recreating fileValidator
  const totalUploadedSizeRef = useRef(totalUploadedSize);

  // Track cumulative size of files being validated in current files upload batch
  const currentBatchSizeRef = useRef(0);

  // Update ref when prop changes
  useEffect(() => {
    totalUploadedSizeRef.current = totalUploadedSize;
  }, [totalUploadedSize]);

  const fileValidator = useCallback(
    file => {
      // Logic to validate cumulative file size start
      // Calculate total file size including previously validated files in this files upload batch
      const newTotalSize = totalUploadedSizeRef.current + currentBatchSizeRef.current + file.size;

      if (newTotalSize > maxFileSizeBytes) {
        return {
          code: FILE_REJECTION_CODES.totalSizeExceeded,
          message: `Adding this file (size ${formatFileSize(file.size)}) would exceed available attachment space. 
            Total attachments on this panel: ${formatFileSize(totalUploadedSizeRef.current + currentBatchSizeRef.current)}. 
            Total combined size of source document and attachments must not exceed ${formatFileSize(maxFileSizeBytes)}.`,
        };
      }

      // If cumulative file size validation passes, add this file's size to the current files upload batch total
      currentBatchSizeRef.current += file.size;

      // Logic to validate cumulative file size ends

      //
      // More custom validations can be added below
      // ...

      return null;
    },
    [maxFileSizeBytes],
  );

  /**
   * Callback fired when files are successfully dropped and pass all validations.
   */
  const onDrop = useCallback(
    acceptedFiles => {
      // Reset batch size for new drop
      currentBatchSizeRef.current = 0;

      // If multiple=true → pass the whole array; otherwise pass the first file
      const payload = multiple ? acceptedFiles : acceptedFiles[0];
      onFileUpload(payload, extraParameters);
    },
    [multiple, extraParameters, onFileUpload],
  );

  // Also reset batch size if drop is rejected
  const onDropRejected = useCallback(() => {
    currentBatchSizeRef.current = 0;
  }, []);

  // Wrap the dropzone configuration in useMemo to prevent unnecessary reinitializations
  const dropzoneConfig = useMemo(() => ({
    onDrop,
    onDropRejected,
    multiple,
    // Keep single-file behavior unless multiple=true. When multiple, allow unlimited
    // unless caller provides a maxFiles cap.
    maxFiles: multiple ? (maxFiles ?? undefined) : 1,
    maxSize: maxFileSizeBytes,
    accept: isAcceptAllFormat ? undefined : acceptFormat,
    disabled,
    // Custom validator runs before onDrop to check cumulative size limit
    validator: fileValidator,
  }), [
    onDrop,
    onDropRejected,
    multiple,
    maxFiles,
    maxFileSizeBytes,
    isAcceptAllFormat,
    acceptFormat,
    disabled,
    fileValidator,
  ]);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone(dropzoneConfig);

  const dropZoneClass = useMemo(() => [
    classes.dropZone,
    isFocused && classes.dropZoneFocused,
    isDragAccept && classes.dropZoneAccept,
    isDragReject && classes.dropZoneReject,
  ].filter(Boolean).join(" "),
  [isFocused, isDragAccept, isDragReject, classes]);

  const formatErrorMessage = (error, file) => {
    switch (error.code) {
      case FILE_REJECTION_CODES.fileTooLarge:
        const actualSize = formatFileSize(file.size);
        const maxSize = formatFileSize(maxFileSizeBytes);
        return `File is too large (${actualSize}). Total combined size of source document and attachments must not exceed ${maxSize}.`;

      case FILE_REJECTION_CODES.totalSizeExceeded: // Add this case
        return error.message;

      default:
        return error.message;
    }
  };

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <div key={file.path}>
      {file.path} - {errors.map(error => formatErrorMessage(error, file)).join(", ")}
    </div>
  ));

  return (
    <div {...getRootProps({ className: dropZoneClass })}>
      <input {...getInputProps()} />
      {disabled && disabledLabel && <p>{disabledLabel}</p>}
      {loading && <LoadingState />}
      {!disabled && !loading && (
        <>
          <Typography variant="subtitle1">
            {label ?? (multiple
              ? "Drag and drop files here, or click to select"
              : "Drag and drop a file here, or click to select")}
          </Typography>
          {helperText && <Typography variant="caption">{helperText}</Typography>}
          {maxFileSizeBytes < 100 * 1024 * 1024 && (
            <Typography variant="caption" color="textSecondary">
              Max combined upload size (source document + attachments): {formatFileSize(maxFileSizeBytes)}
            </Typography>
          )}
          <CloudFileUpload size={48} />
          {fileRejectionItems.length > 0
            && (
            <Typography
              variant="caption"
              color="error"
              style={{ whiteSpace: "pre-line" }} // Preserves line breaks from template literals in error messages
            >
              {fileRejectionItems}
            </Typography>
            )}
        </>
      )}
    </div>
  );
}

export default DropzoneAttachment;
